import React, { useMemo } from 'react';
import { normalizeMatrixCell } from '../utils/matrixDisplay';
import './css/MatrixGrid.css';

function MatrixGrid({
    matrix,
    colCount = null,
    useCompactLayout = false,
    animated = false,
    stage = 'show',
    cellDelays = {},
    selectedCellId = null,
    canClick = null,
    onCellClick = null,
    containerRef = null,
    interactive = false,
}) {
    const effectiveColCount = useMemo(() => {
        if (colCount != null) return colCount;
        if (!Array.isArray(matrix)) return 0;
        return Math.max(0, ...matrix.map((row) => (row?.length) || 0));
    }, [matrix, colCount]);

    const tiles = useMemo(() => {
        if (!Array.isArray(matrix)) return [];
        return matrix.flatMap((row, rowIndex) =>
            (row || [])
                .map((cell, colIndex) => {
                    const normalized = normalizeMatrixCell(cell);
                    return normalized ? { cell: normalized, rowIndex, colIndex } : null;
                })
                .filter(Boolean)
        );
    }, [matrix]);

    if (!Array.isArray(matrix) || matrix.length === 0) {
        return null;
    }

    const containerStage = stage === 'exit' ? 'exit' : stage === 'hidden' ? 'hidden' : 'show';
    const containerClasses = [
        'matrix-grid',
        containerStage,
        useCompactLayout ? 'compact-layout' : 'standard-layout',
        animated ? '' : 'is-static',
        interactive ? 'is-interactive' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={containerRef}
            className={containerClasses}
            style={!useCompactLayout ? { '--matrix-columns': effectiveColCount } : undefined}
        >
            {tiles.map(({ cell, rowIndex, colIndex }) => {
                const delayObj = cellDelays[cell.id] || {};
                const { inIndex = 0, outIndex = 0 } = delayObj;
                const animationIndex = stage === 'exit' ? outIndex : inIndex;
                const stagger = stage === 'exit' ? 0.02 : 0.03;
                const delaySeconds = animated ? (animationIndex * stagger).toFixed(2) : '0';

                const isSelected = selectedCellId !== null && String(selectedCellId) === String(cell.id);
                const isClickable = typeof canClick === 'function' && canClick(cell);

                const cellStyle = animated ? { transitionDelay: `${delaySeconds}s` } : {};
                if (!useCompactLayout) {
                    cellStyle.gridColumn = colIndex + 1;
                    cellStyle.gridRow = rowIndex + 1;
                }

                const cellClasses = [
                    'matrix-cell',
                    containerStage,
                    cell.played ? 'played' : '',
                    isSelected ? 'active-cell' : '',
                    isClickable ? 'is-clickable' : '',
                ]
                    .filter(Boolean)
                    .join(' ');

                return (
                    <div
                        key={cell.id}
                        title={cell.frage || undefined}
                        className={cellClasses}
                        style={cellStyle}
                        onClick={() => {
                            if (isClickable && typeof onCellClick === 'function') {
                                onCellClick(cell);
                            }
                        }}
                    >
                        <div className="matrix-cell-inner">
                            {!cell.played && (
                                <div className="matrix-points" aria-hidden="true">{cell.points}</div>
                            )}
                            <div className="matrix-category">{cell.thema}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default MatrixGrid;
